import { FC, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { FormCategory, formatCurrencyFromCents } from "@simplyk/common";

import { printAuctionQrCards } from "./helpers/printAuctionQrCards";
import { PrintableAuctionQrCard } from "./PrintableAuctionQrCard/PrintableAuctionQrCard";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { trpc } from "@/common/helpers/trpc";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardInfobox } from "@/dashboard-design-system/Infobox";

interface ItemsQrCodeContentProps {
  formId: string;
  formLink: string;
  formCategory: FormCategory;
}

export const ItemsQrCodeContent: FC<ItemsQrCodeContentProps> = ({ formId, formLink, formCategory }) => {
  const { t } = useTranslate();
  const { logAmplitudeEvent } = useAmplitude();
  const { isoLocale, locale } = useLocaleContext();

  const [isPrinting, setIsPrinting] = useState(false);

  const { data: auctionItemsData, isLoading: isLoadingItems } = trpc.getAuctionItemsForSharing.useQuery(
    { ticketingId: formId, locale },
    { enabled: Boolean(formId) }
  );

  const handlePrint = async () => {
    if (!auctionItemsData?.items?.length || isPrinting) {
      return;
    }

    logAmplitudeEvent(AmplitudeEvents.ShareFormModalClickSharingQrCode, {
      form_type: formCategory,
      qr_code_sharing_type: "Print items",
    });

    const items = auctionItemsData.items.map((item) => ({
      title: item.title,
      formLink: `${formLink}?auctionItem=${item.id}`,
      startingBidLine: t("common", "shareFormModal.qrCode.items.printCard.startingBid", {
        amount: formatCurrencyFromCents(item.amount, {
          isoLocale,
          currency: auctionItemsData.currency,
        }),
      }),
      minimumBidIncrementLine: item.bidIncrement
        ? t("common", "shareFormModal.qrCode.items.printCard.minimumBidIncrement", {
            amount: formatCurrencyFromCents(item.bidIncrement, {
              isoLocale,
              currency: auctionItemsData.currency,
            }),
          })
        : null,
    }));

    setIsPrinting(true);
    try {
      await printAuctionQrCards({
        items,
        organizationName: auctionItemsData.organizationName,
        organizationLogoUrl: auctionItemsData.organizationLogoUrl,
        hostedByLabel: t("common", "shareFormModal.qrCode.items.printCard.hostedBy"),
      });
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <Stack gap={2}>
      <DashboardInfobox
        vibe="brand"
        type="alert"
        size="medium"
        pointer="bottom"
        title={t("common", "shareFormModal.qrCode.items.infoBox.title")}
        hideIcon
      >
        {t("common", "shareFormModal.qrCode.items.infoBox.content")}
      </DashboardInfobox>
      <Stack alignItems="center" gap={2}>
        <Box sx={{ position: "relative", width: 111, height: 148 }}>
          <Box sx={{ position: "absolute", top: 16, left: 15, zIndex: 0 }}>
            <PrintableAuctionQrCard />
          </Box>
          <Box sx={{ position: "absolute", top: 8, left: 7, zIndex: 1 }}>
            <PrintableAuctionQrCard />
          </Box>
          <Box sx={{ position: "absolute", top: 0, left: 0, zIndex: 2 }}>
            <PrintableAuctionQrCard />
          </Box>
        </Box>
        <DashboardButton
          size="large"
          vibe="brand"
          variant="filled"
          onClick={handlePrint}
          isLoading={isPrinting || isLoadingItems}
          disabled={!auctionItemsData?.items?.length}
        >
          {t("common", "shareFormModal.qrCode.items.printButton")}
        </DashboardButton>
      </Stack>
    </Stack>
  );
};
